import React, { useState } from "react";
import { TextField, TextFieldProps } from "@mui/material";

/**
 * Formats digits into +1 (XXX) XXX-XXXX
 * Only keeps digits, always prefixes with +1
 */
function formatPhone(value: string): string {
  // Strip everything except digits
  const digits = value.replace(/\D/g, "");

  // Remove leading "1" if present (we add it ourselves)
  const cleaned = digits.startsWith("1") ? digits.slice(1) : digits;

  // Limit to 10 digits
  const limited = cleaned.slice(0, 10);

  if (limited.length === 0) return "";
  if (limited.length <= 3) return `+1 (${limited}`;
  if (limited.length <= 6) return `+1 (${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `+1 (${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

function isComplete(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  const cleaned = digits.startsWith("1") ? digits.slice(1) : digits;
  return cleaned.length === 10;
}

type PhoneInputProps = Omit<TextFieldProps, "onChange" | "value"> & {
  value?: string;
  defaultValue?: string;
  onChange?: (formatted: string) => void;
  name?: string;
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value: controlledValue,
  defaultValue,
  onChange,
  name,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState(
    formatPhone(defaultValue || controlledValue || "")
  );

  const displayValue = controlledValue !== undefined
    ? formatPhone(controlledValue)
    : internalValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setInternalValue(formatted);
    if (onChange) {
      onChange(formatted);
    }
  };

  const complete = isComplete(displayValue);
  const hasInput = displayValue.length > 0;

  return (
    <>
      <TextField
        {...rest}
        value={displayValue}
        onChange={handleChange}
        placeholder="+1 (707) 333-5689"
        error={hasInput && !complete ? true : rest.error}
        helperText={
          hasInput && !complete
            ? "Format: +1 (707) 333-5689"
            : rest.helperText
        }
        inputProps={{
          ...rest.inputProps,
          maxLength: 17, // "+1 (XXX) XXX-XXXX"
        }}
      />
      {/* Hidden input for form submission with the formatted value */}
      {name && <input type="hidden" name={name} value={displayValue} />}
    </>
  );
};
